import { Alert, AlertTitle, Box, Button, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getUserTermsSettings, updateUserTermsSettings } from 'src/shared/services/userService';

type TermsForm = {
  termsVersion: string;
  termsText: string;
};

export const UserTermsSettingsPage = () => {
  const { t } = useTranslation(['user', 'common']);
  const isMounted = useIsMounted();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [requireTerms, setRequireTerms] = useState<boolean>(false);

  const formMethods = useForm<TermsForm>({
    mode: 'all',
    defaultValues: { termsVersion: '', termsText: '' },
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getUserTermsSettings();
        if (isMounted()) {
          setRequireTerms(res.data.requireTermsAcceptance ?? false);
          reset({ termsVersion: res.data.termsVersion ?? '', termsText: res.data.termsText ?? '' });
          setLoaded(true);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setLoaded(true);
        }
      }
    };
    fetchData();
  }, [isMounted, reset]);

  const onSubmit = async (data: TermsForm) => {
    setSaving(true);
    try {
      await updateUserTermsSettings({
        requireTermsAcceptance: requireTerms,
        termsVersion: data.termsVersion,
        termsText: data.termsText,
      });
      toast.success(t('common:statusMessages.saveChangesSuccess') as string);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('common:statusMessages.saveChangesError') as string);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormLoaderWrapper loading={!loaded || saving}>
      <Box sx={{ p: 3 }}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={12} md={4}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {t('user:terms.sectionTitle')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {t('user:terms.sectionDescription')}
                </Typography>

                <Alert severity='info' sx={{ mt: 2 }}>
                  <AlertTitle>{t('user:terms.scopeTitle')}</AlertTitle>
                  <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
                    <li>{t('user:terms.scopeUsers')}</li>
                    <li>{t('user:terms.scopeAdmins')}</li>
                    <li>{t('user:terms.scopePnr')}</li>
                    <li>{t('user:terms.scopeMembers')}</li>
                    <li>{t('user:terms.scopeVersion')}</li>
                  </Box>
                </Alert>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControlLabel
                  control={<Switch checked={requireTerms} onChange={(e) => setRequireTerms(e.target.checked)} />}
                  label={t('user:terms.requireLabel')}
                />

                {requireTerms && (
                  <>
                    <ControlledTextField name='termsVersion' label={t('user:terms.version')} size='small' sx={{ mt: 3, mb: 2 }} />
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      {t('user:terms.text')}
                    </Typography>
                    {loaded && <ControlledTextEditor name='termsText' rows={25} />}
                  </>
                )}
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant='contained' disableElevation type='submit' disabled={saving || !loaded}>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </FormLoaderWrapper>
  );
};
